/**
 * Organization Context (single-domain mode)
 *
 * Behaviour changed Feb 2026: the URL no longer carries an org slug. All
 * logins land on `app.elevante.ai/login` and the active organisation is
 * derived from the authenticated user's `orgId` (or, for multi-org users,
 * from the post-login picker stored in `localStorage.currentOrgId`).
 *
 * This file is deliberately kept API-compatible with the previous
 * slug-parsing implementation so that every consumer of `useOrg()` —
 * `buildPath()`, `navigateWithOrg()`, `orgSlug`, `setOrganization()` —
 * continues to work without refactoring dozens of components.
 *
 *   - `buildPath(path)` returns `path` unchanged (no slug prefix)
 *   - `orgSlug` is sourced from the resolved org doc, NOT the URL
 *   - `getOrgSlugFromPath` / `getPathWithoutOrg` remain exported only so
 *     the SlugRedirect component can strip legacy `/acme/...` URLs
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/client';

const OrgContext = createContext(null);

// Keep RESERVED_ROUTES exported so SlugRedirect knows which first-path
// segments are app routes, not tenant slugs. Sync with App.js routes.
export const RESERVED_ROUTES = [
  'login', 'register', 'forgot-password', 'reset-password', 'self-onboard',
  'dashboard', 'courses', 'labs', 'analytics', 'content-builder', 'help',
  'users', 'settings', 'superadmin', 'orgs', 'api',
  'industry-lab-configurator', 'industry-labs', 'my-certificates', 'auth',
  'admin', 'notifications', 'community', 'support', 'achievements',
  'profile', 'video', 'live-session', 'grading', 'gamification',
  'instructor', 'my-analytics', 'select-org',
  'null', 'undefined'
];

/** Helper used by SlugRedirect — returns the first segment IF it looks
 *  like a tenant slug and isn't a reserved app route. */
export function getOrgSlugFromPath(pathname) {
  const parts = pathname.replace(/^\//, '').split('/');
  if (parts.length === 0 || !parts[0]) return null;
  const potentialSlug = parts[0].toLowerCase();
  if (RESERVED_ROUTES.includes(potentialSlug)) return null;
  // Only accept lowercase alphanumeric + dash slugs.
  if (!/^[a-z0-9-]+$/.test(potentialSlug)) return null;
  return potentialSlug;
}

/** Strip the leading `/{slug}` from a pathname (e.g. for SlugRedirect). */
export function getPathWithoutOrg(pathname, slug) {
  if (!slug) return pathname;
  const regex = new RegExp(`^\\/${slug}(?=\\/|$)`, 'i');
  return pathname.replace(regex, '') || '/';
}

export const OrgProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hydrate currentOrg on mount from `localStorage.currentOrgId` (set at
  // login / multi-org picker / explicit org switch). No URL parsing.
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      const storedOrgId = localStorage.getItem('currentOrgId');
      const token = localStorage.getItem('accessToken');
      // Never resolve an org before the user is authenticated (e.g. on the
      // login page, or mid multi-org pick). Without a token this would 401/403
      // on GET /organizations/{id}; with a stale token it would 403 on org
      // mismatch. Either way there is no valid "current org" yet.
      if (!storedOrgId || !token) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        // Reuse the resolve endpoint with subdomain param for lookups —
        // the backend also accepts org-id lookups via /organizations/{id}
        const res = await api.get(`/organizations/${storedOrgId}`);
        if (!cancelled) setCurrentOrg(res.data || null);
      } catch (err) {
        // Stale orgId in localStorage — clear it silently.
        if (!cancelled) {
          localStorage.removeItem('currentOrgId');
          localStorage.removeItem('currentOrgSlug');
          setError(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, []);

  // Org slug is derived from the resolved org doc (for display/analytics
  // only — never affects routing).
  const orgSlug = currentOrg?.subdomain || currentOrg?.slug || null;

  /** No-op path builder. Kept for API compatibility with every component
   *  that still calls `buildPath('/foo')` — they all just get '/foo'. */
  const buildPath = useCallback((path) => path, []);

  /** Navigation helper now just delegates to react-router. */
  const navigateWithOrg = useCallback((path, options = {}) => {
    navigate(path, options);
  }, [navigate]);

  /** Called by the login picker, org switcher, etc. */
  const setOrganization = useCallback(async (org) => {
    if (org) {
      setCurrentOrg(org);
      if (org.id) localStorage.setItem('currentOrgId', org.id);
      const newSlug = org.subdomain || org.slug || null;
      if (newSlug) {
        localStorage.setItem('currentOrgSlug', newSlug);
      } else {
        localStorage.removeItem('currentOrgSlug');
      }
    } else {
      setCurrentOrg(null);
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgSlug');
    }
  }, []);

  const clearOrg = useCallback(() => {
    setCurrentOrg(null);
    localStorage.removeItem('currentOrgId');
    localStorage.removeItem('currentOrgSlug');
  }, []);

  const value = {
    currentOrg,
    orgSlug,            // display-only; do NOT use in routing
    // In single-domain mode every route is already slug-less, so the
    // "path without org" is just the current pathname. Kept non-null so
    // consumers calling `actualPath.includes(...)` (e.g. MainLayout nav
    // highlighting) don't NPE.
    actualPath: location.pathname,
    loading,
    error,
    hasOrg: !!currentOrg,
    navigateWithOrg,
    setOrganization,
    clearOrg,
    buildPath,
    getPathWithoutOrg: (pathname) => pathname,  // no-op
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

export default OrgContext;
